import React from 'react';

/**
 * Agnostic "big block" card used across the viewers' summary screens.
 *
 * Renders a rounded bordered card with a header (icon avatar + title + optional
 * subtitle + optional right-aligned action) and free-form children below — the
 * consumer arranges its own grid of <StatTileNew /> inside.
 *
 * Props:
 *  - icon?        : node rendered inside the avatar
 *  - title        : card title
 *  - subtitle?    : muted second line (e.g. "Semaine du … · vs période précédente")
 *  - action?      : node aligned to the right of the header (badge, button…)
 *  - accentClass? : Tailwind classes for the avatar (bg + text). Defaults to blue.
 *  - className?   : extra classes on the card wrapper
 */
const StatCard = ({
  icon,
  title,
  subtitle,
  action = null,
  accentClass = 'bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-300',
  className = '',
  children,
}) => {
  const hasHeader = title || icon || action;
  return (
    <div className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl p-5 ${className}`}>
      {hasHeader && (
        <div className="flex items-center gap-3 mb-4">
          {icon && (
            <span className={`inline-flex items-center justify-center w-9 h-9 rounded-xl flex-none ${accentClass}`} aria-hidden>
              {React.cloneElement(icon, { className: 'w-[18px] h-[18px]', strokeWidth: 2 })}
            </span>
          )}
          <div className="min-w-0 flex-1 text-left">
            {title && <h3 className="text-lg font-bold text-gray-900 dark:text-white leading-tight">{title}</h3>}
            {subtitle && <p className="text-sm text-gray-500 dark:text-gray-400 leading-tight">{subtitle}</p>}
          </div>
          {action}
        </div>
      )}
      {children}
    </div>
  );
};

export default StatCard;
